import type { Dayjs } from "@marekl/adminer/datetime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost, apiPut, ApiError } from "../../lib/api";
import type { Person } from "./memberships";
import type { ID } from "../id";

export type TeamStatus = "pending" | "active" | "suspended";
export type TeamKind = "full" | "individual";

// The leader is a Person plus the two facts that are not Person data:
// when the leadership started, and whether a login exists. The person is
// nested (not flattened) so it can be handed straight to the shared member
// dialog — see leaderDTO in internal/handlers/api/teams.go.
export interface TeamLeader {
  person: Person;
  user_id: ID | null;
  valid_from: Dayjs;
  // The User's login credential. Starts identical to person.email (public
  // registration writes one value into both) and drifts once either is
  // edited. Never write this back into the person — it is the login, not
  // their contact e-mail.
  login_email: string;
}

export interface BillingInfo {
  id: ID;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  subject_name: string;
  street: string;
  city: string;
  postal_code: string;
  country_code: string;
  ico: string;
  dic: string;
}

export interface Team {
  id: ID;
  federation_id?: ID | null;
  federation_name?: string;
  federation_short_name?: string;
  // Whether the team belongs to ČNSMS. The client keys „is this a ČNSMS team"
  // on the flag, never on the id: the id is per installation, the flag is
  // the fact.
  federation_is_primary: boolean;
  // The umbrella (WMS). With is_primary and a missing federation_id it says
  // which teams the central staff run (rbac.CanAdministerTeam): a
  // members_admin manages those, a national federation's teams are not theirs.
  federation_is_umbrella?: boolean;
  kind: TeamKind;
  status: TeamStatus;
  name: string;
  // The state the club represents (ISO 3166-1 alpha-2), which is what the
  // flag beside a foreign club's name renders. Not the billing country.
  country_code: string;
  // Which door the club came through when it signed itself up. Rendered only
  // while the team is pending (appliedFlowBadge): it says what was ASKED
  // FOR, and once the club is active what matters is what the roster says.
  // Absent for a team an admin created, and for any that predates it.
  applied_flow?: string;
  public_id: string;
  billing_info_id?: ID | null;
  billing_info?: BillingInfo | null;
  cmf_id?: number | null;
  members_missing_cmf_id?: number;
  leader?: TeamLeader | null;
  // Membership-fee payment state in the active season (see memberState.ts).
  payment_status?: PaymentStatus;
  paid_at?: Dayjs | null;
  // Condensed state for the active season, computed only for active teams
  // ("not_registered" | "cup_only" | "unpaid" | "missing_photo"; empty =
  // ready). Ask hasNoSeasonRegistration rather than one key: the first two
  // both mean „nobody registered this season".
  // members_registered / members_with_photo back the "X/Y má fotku" tooltip.
  // Both count registered members only — guests never contribute.
  state_issues?: string[];
  members_registered?: number;
  members_with_photo?: number;
  // Everyone on the active season's soupiska, of any kind and for a team of
  // any status: the size of the club, which members_registered is not for a
  // cup-only or foreign one.
  members_total?: number;
  // When the team was filed; for a pending one, when its application came in.
  created_at?: Dayjs;
}

export type PaymentStatus = "unpaid" | "paid";

interface ListResponse {
  data: Team[];
  total?: number;
}

export interface TeamFilters {
  status?: TeamStatus;
  federation_id?: ID;
  q?: string;
}

// `options.enabled`: GET /teams is admin / members_admin only. See the note on
// useAuditLogs — gate the query, not the render.
export function useTeams(filters: TeamFilters = {}, options?: { enabled?: boolean }) {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.federation_id) params.set("federation_id", String(filters.federation_id));
  if (filters.q) params.set("q", filters.q);
  const qs = params.toString();
  return useQuery<Team[]>({
    queryKey: ["teams", filters],
    queryFn: async () => {
      const res = await apiGet<ListResponse>(`/teams${qs ? "?" + qs : ""}`);
      return res.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useTeam(id: ID | undefined) {
  return useQuery<Team>({
    queryKey: ["teams", id],
    enabled: !!id,
    queryFn: () => apiGet<Team>(`/teams/${id}`),
  });
}

export function useMyTeams() {
  return useQuery<Team[]>({
    queryKey: ["me", "teams"],
    queryFn: async () => {
      const res = await apiGet<ListResponse>("/me/teams");
      return res.data;
    },
  });
}

export function useApproveTeam() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, ID>({
    mutationFn: (id) => apiPost<Team>(`/teams/${id}/approve`, {}),
    // Reported inside the confirm dialog, which stays open on a refusal.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["me", "teams"] });
    },
  });
}

export function useSuspendTeam() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, { id: ID; reason?: string }>({
    mutationFn: ({ id, reason }) => apiPost<Team>(`/teams/${id}/suspend`, { reason: reason ?? "" }),
    // Reported inside the confirm dialog, which stays open on a refusal.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["me", "teams"] });
    },
  });
}

export interface CreateTeamPayload {
  name: string;
  country_code: string;
  federation_id: ID;
}

/**
 * Files a team directly, active and with no approval step — how a
 * federation enters its own clubs. The leader, the roster and the billing
 * details follow by their own routes.
 */
export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, CreateTeamPayload>({
    mutationFn: (payload) => apiPost<Team>("/teams", payload),
    // The dialog binds the errors to its fields (apiErrorToFormErrors).
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}

/**
 * Re-files a team under another federation: how a state's federation takes
 * over the clubs that signed up under the umbrella before it existed. The
 * server refuses a move into or out of ČNSMS (422).
 */
export function useMoveTeamFederation() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, { id: ID; federation_id: ID }>({
    mutationFn: ({ id, federation_id }) =>
      apiPost<Team>(`/teams/${id}/federation`, { federation_id }),
    // Reported inside the dialog, which stays open on a refusal.
    meta: { suppressErrorToast: true },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["teams", id] });
    },
  });
}

/**
 * Renames a team. Open to whoever administers it (admin, members_admin on
 * ČNSMS and the umbrella, the team's federation_admin), never its leader.
 */
export function useRenameTeam() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, { id: ID; name: string }>({
    mutationFn: ({ id, name }) => apiPut<Team>(`/teams/${id}/name`, { name }),
    // Reported inside the dialog, which stays open on a refusal.
    meta: { suppressErrorToast: true },
    // Everything: the name rides on entries, start lists, dashboard alerts
    // and role scopes, and a rename is rare enough not to pick them out.
    onSuccess: () => qc.invalidateQueries(),
  });
}

export interface UpdateBillingPayload {
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  subject_name: string;
  street: string;
  city: string;
  postal_code: string;
  country_code: string;
  ico: string;
  dic: string;
}

export function useUpdateTeamBilling(teamId: ID) {
  const qc = useQueryClient();
  return useMutation<BillingInfo, ApiError, UpdateBillingPayload>({
    mutationFn: (payload) =>
      apiPatch<BillingInfo>(`/teams/${teamId}/billing`, payload),
    // EditBillingModal binds errors to its form fields (apiErrorToFormErrors).
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams", teamId] });
      qc.invalidateQueries({ queryKey: ["teams"] });
    },
  });
}

/**
 * SetLeaderPayload hands the team to another of its registered members.
 *
 * `login` provisions the account the new leader signs in with and is sent
 * only for someone who has none: leadership is what grants the team_leader
 * role, so a leader without a login is a team nobody can administer. The
 * server refuses it (409) if the person already has an account — moving or
 * resetting one is the users admin's job.
 */
export interface SetLeaderPayload {
  person_id: ID;
  // No password: the server creates the account unusable and its owner sets
  // one from a mailed link, so the secret never passes through whoever is
  // doing the handover.
  login?: {
    email: string;
    display_name: string;
  };
}

export interface SetLeaderResult {
  person_id: ID;
  user_id?: ID;
  login_created: boolean;
  // True when the new leader was an asistent: a vedoucí cannot hold that
  // role, so the handover drops it (membership.validateRoles refuses the
  // combination on every later edit).
  assistant_role_dropped: boolean;
  // Present ONLY when the server has no letterman configured, in which case
  // the admin has to relay the link by hand. With mail working it is absent
  // and the secret never reaches this screen.
  set_password_url?: string;
}

export function useSetTeamLeader(teamId: ID) {
  const qc = useQueryClient();
  return useMutation<SetLeaderResult, ApiError, SetLeaderPayload>({
    mutationFn: (payload) => apiPut<SetLeaderResult>(`/teams/${teamId}/leader`, payload),
    // ChangeLeaderModal renders the error inline; the global toast would say
    // the same thing twice.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams", teamId] });
      qc.invalidateQueries({ queryKey: ["teams"] });
      qc.invalidateQueries({ queryKey: ["me", "teams"] });
      // The derived team_leader role moved with the leadership row, so any
      // cached view of who may do what is now wrong.
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["persons"] });
    },
  });
}

/** The account is named after the leader server-side. */
export interface CreateLeaderLoginPayload {
  email: string;
}

export interface CreateLeaderLoginResult {
  user_id: ID;
  email: string;
  /** See SetLeaderResult.set_password_url. */
  set_password_url?: string;
}

/** Gives the team's current leader the account they sign in with. */
export function useCreateLeaderLogin(teamId: ID) {
  const qc = useQueryClient();
  return useMutation<CreateLeaderLoginResult, ApiError, CreateLeaderLoginPayload>({
    mutationFn: (payload) => apiPost(`/teams/${teamId}/leader/login`, payload),
    // CreateLeaderLoginModal renders the error inline.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["teams", teamId] });
      qc.invalidateQueries({ queryKey: ["persons"] });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export function useAssignTeamCmfId() {
  const qc = useQueryClient();
  return useMutation<Team, ApiError, ID>({
    mutationFn: (id) => apiPost<Team>(`/teams/${id}/cmf-id`, {}),
    // Reported inside the confirm dialog, which stays open on a refusal.
    meta: { suppressErrorToast: true },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["teams", id] });
      qc.invalidateQueries({ queryKey: ["teams"] });
      // Members table shows Person CmfID which may have just been
      // backfilled — refresh the member list as well.
      qc.invalidateQueries({ queryKey: ["teams", id, "members"] });
    },
  });
}
